import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react'
import { Icon } from './Icon'
import { Tooltip } from './Tooltip'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// Sunday-first week: index 0 = Sun, 6 = Sat
const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

/** Holiday entry. Back-compat: callers may pass plain ISO strings too. */
export interface HolidayEntry { date: string; name: string }

/** Back-compat normaliser: `string` → `{ date, name: '' }`. */
function normaliseHolidays(raw: ReadonlyArray<string | HolidayEntry> | undefined): HolidayEntry[] {
  if (!raw) return []
  return raw.map((h) => (typeof h === 'string' ? { date: h, name: '' } : h))
}

/** An employee who is out on a given day. */
export interface PersonOut { id: string; name: string }

/** A blackout window day with its window name (e.g. "Year-end close"). */
export interface BlackoutEntry { date: string; name: string }

/**
 * Color tokens used to tint endpoints and range fill. Defaults to the
 * terracotta accent used by the standalone primitive; the LMS apply flow
 * passes leave-type swatch / bg / line / ink so the calendar retints live
 * as the selected leave type changes.
 */
export interface CalendarAccent {
  /** Saturated hue used for endpoint circle fill. */
  swatch: string
  /** Soft tinted fill used for the in-range bar. */
  bg: string
  /** Border color for soft fills (currently unused but reserved). */
  line: string
  /** Text color used on top of `bg` (currently unused but reserved). */
  ink: string
}

const DEFAULT_ACCENT: CalendarAccent = {
  swatch: 'var(--steel-blue)',
  bg: 'var(--primary-bg)',
  line: 'var(--steel-blue)',
  ink: 'var(--text)',
}

function buildPeopleOutLabel(people: PersonOut[]): string {
  if (people.length === 0) return ''
  const shown = people.slice(0, 3).map((p) => p.name)
  const extra = people.length - shown.length
  const names = shown.join(', ')
  return extra > 0
    ? `${people.length} people out: ${names} +${extra} more`
    : `${people.length} ${people.length === 1 ? 'person' : 'people'} out: ${names}`
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

function daysInMonth(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate()
}

/**
 * Column index for a JS Date.getDay() in a Sunday-first grid (0=Sun…6=Sat) —
 * identity, kept named so the week-start convention reads at the call sites.
 */
function dowSundayFirst(jsDow: number): number {
  return jsDow
}

function addDays(iso: string, delta: number): string {
  const d = new Date(iso)
  d.setDate(d.getDate() + delta)
  return d.toISOString().slice(0, 10)
}


export interface CalendarRangePickerProps {
  from: string
  to: string
  onChange: (from: string, to: string) => void
  min?: string | undefined
  /** Latest selectable ISO date (inclusive). Dates after this are grayed out
   *  and not clickable. Used to enforce the advance-booking-window cap. */
  max?: string | undefined
  /** Single-date mode: picking one date fires onChange(date, date) immediately. */
  single?: boolean | undefined
  /** One-click single-day mode (apply-leave calendar). When ON, the first
   *  click on a date completes a 1-day range immediately —
   *  `onChange(iso, iso)` — so the caller can surface AM/PM/Full day-part
   *  options right away instead of requiring a second click on the same date.
   *  Once a complete range exists, clicking a later date extends the end in
   *  one click (multi-day stays a two-step gesture only when no range is set
   *  yet and the user clicks an earlier/same date, which restarts). Default
   *  OFF so every other caller keeps the two-click start→end contract. */
  oneClickSingleDay?: boolean | undefined
  /**
   * ISO dates to mark with a holiday dot.
   * Accepts either plain ISO strings (back-compat) or { date, name } objects
   * (shows a tooltip with the holiday name on hover).
   */
  publicHolidays?: ReadonlyArray<string | HolidayEntry> | undefined
  /**
   * People who are out on each date, keyed by ISO date string.
   * When a day has entries a tooltip "N people out: …" is shown on hover.
   * Existing callers can omit this prop — defaults to empty map.
   */
  peopleOut?: ReadonlyMap<string, PersonOut[]> | Record<string, PersonOut[]> | undefined
  /**
   * Color override for endpoints + range fill. Defaults to terracotta when
   * omitted. Pass leave-type tokens here to tint the calendar to match the
   * selected leave type.
   */
  accent?: CalendarAccent | undefined
  /**
   * Controlled month — when provided, internal view-month state is bypassed
   * and `onViewMonthChange` fires instead. Use this when an outer wrapper
   * (e.g. RangeCalendar) coordinates two side-by-side pickers.
   */
  viewYear?: number | undefined
  viewMonth?: number | undefined
  onViewMonthChange?: ((year: number, month: number) => void) | undefined
  /**
   * Hide the built-in prev/next nav. Useful when the outer wrapper renders
   * a single shared nav row spanning multiple month grids.
   */
  hideNav?: boolean | undefined
  /**
   * Blackout windows: dates that are still selectable but trigger
   * escalation. Rendered with a hatched background + clay-dot marker
   * + tooltip naming the window.
   */
  blackoutDates?: ReadonlyArray<BlackoutEntry> | undefined
  /**
   * Conflict dates: dates where teammates are already off. Rendered with
   * an ink dot under the day number.
   */
  conflictDates?: ReadonlyArray<string> | undefined
  /**
   * Controlled hover date — when provided, internal hover state is bypassed.
   * Use this when an outer wrapper (e.g. RangeCalendar) needs to share hover
   * across two side-by-side pickers so the range highlight spans both months.
   */
  hoverDate?: string | null | undefined
  onHoverChange?: ((date: string | null) => void) | undefined
  /**
   * Overflow split: first ISO date that draws from Unpaid Leave instead of
   * the original-type balance. When set, in-range days on or after this
   * date render with a desaturated "unpaid" bar so the employee can see
   * exactly which calendar days will be filed as Unpaid Leave. The
   * original-type endpoint circle stays accented; only the bar tint
   * shifts at the boundary. Pass `null`/`undefined` to disable the
   * treatment entirely.
   */
  unpaidFromDate?: string | null | undefined
  /**
   * When true, drop the 520px max-width cap on the calendar wrapper so it
   * stretches to fill its parent. Used by RangeCalendar on the Apply page
   * where each month sits inside a card whose width is dictated by the
   * page grid, not by a popover. Default false preserves the bounded
   * popover behaviour for DateField / DateRangeFilter callers.
   */
  fillWidth?: boolean | undefined
  /**
   * Cap the selectable range to this many days from the picked start. Once a
   * start is chosen, dates that would push the span over the cap are disabled.
   * Used to limit a balance-capped leave type (e.g. Sick with 3 days left) to
   * its remaining balance. Counts working days by default; set
   * ``spanCountsElapsedDays`` for event-bound (calendar-day) leave types.
   */
  maxSpanDays?: number | undefined
  /** When true, ``maxSpanDays`` counts elapsed (calendar) days incl. weekends
   *  + holidays, instead of working days. */
  spanCountsElapsedDays?: boolean | undefined
}

/**
 * Click-to-range calendar with Sunday week-start, 44px touch targets,
 * people-out and holiday-name tooltips, and full keyboard arrow navigation (ARIA grid).
 */
export function CalendarRangePicker({
  from,
  to,
  onChange,
  min,
  max,
  single = false,
  oneClickSingleDay = false,
  publicHolidays,
  peopleOut,
  accent = DEFAULT_ACCENT,
  viewYear: viewYearProp,
  viewMonth: viewMonthProp,
  onViewMonthChange,
  hideNav = false,
  blackoutDates,
  conflictDates,
  hoverDate,
  onHoverChange,
  unpaidFromDate,
  fillWidth = false,
  maxSpanDays,
  spanCountsElapsedDays = false,
}: CalendarRangePickerProps): JSX.Element {
  const uid = useId()
  const gridId = `crp-grid-${uid.replace(/:/g, '')}`

  const holidays = normaliseHolidays(publicHolidays)
  const holidayMap = new Map(holidays.map((h) => [h.date, h.name]))

  // Balance cap: once a start is picked, disable any end date whose span from
  // the start exceeds maxSpanDays (working days, or elapsed days for
  // event-bound types). Dates on/before the start are never capped — clicking
  // them just re-picks the start.
  const spanExceedsCap = (iso: string): boolean => {
    // Single-select picks exactly one day — it can never span, so the cap never
    // applies. Without this, a balance that funds only one day would render
    // every later date disabled, trapping the pick on its first date.
    if (single) return false
    if (maxSpanDays === undefined || from === '' || iso <= from) return false
    const start = new Date(`${from}T00:00:00`)
    const end = new Date(`${iso}T00:00:00`)
    if (spanCountsElapsedDays) {
      const elapsed = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
      return elapsed > maxSpanDays
    }
    let working = 0
    const cur = new Date(start)
    while (cur <= end) {
      const dow = cur.getDay()
      const curIso = isoDate(cur.getFullYear(), cur.getMonth(), cur.getDate())
      if (dow !== 0 && dow !== 6 && !holidayMap.has(curIso)) working += 1
      cur.setDate(cur.getDate() + 1)
    }
    return working > maxSpanDays
  }

  const blackoutMap = new Map((blackoutDates ?? []).map((b) => [b.date, b.name]))
  const conflictSet = new Set(conflictDates ?? [])

  // Normalise peopleOut to a plain Map for O(1) lookup
  const peopleOutMap: Map<string, PersonOut[]> = (() => {
    if (!peopleOut) return new Map()
    if (peopleOut instanceof Map) return peopleOut
    return new Map(Object.entries(peopleOut))
  })()

  const todayIso = isoToday()
  const anchor = from || todayIso
  const [internalYear, setInternalYear] = useState(() => Number(anchor.slice(0, 4)))
  const [internalMonth, setInternalMonth] = useState(() => Number(anchor.slice(5, 7)) - 1)

  const isControlled = viewYearProp !== undefined && viewMonthProp !== undefined
  const viewYear = isControlled ? viewYearProp : internalYear
  const viewMonth = isControlled ? viewMonthProp : internalMonth

  const setView = useCallback(
    (y: number, m: number): void => {
      if (isControlled) {
        onViewMonthChange?.(y, m)
      } else {
        setInternalYear(y)
        setInternalMonth(m)
      }
    },
    [isControlled, onViewMonthChange],
  )

  const [internalHover, setInternalHover] = useState<string | null>(null)
  const isHoverControlled = hoverDate !== undefined
  const hover = isHoverControlled ? hoverDate : internalHover

  function setHover(date: string | null): void {
    if (isHoverControlled) onHoverChange?.(date)
    else setInternalHover(date)
  }

  const [focusedDay, setFocusedDay] = useState<string | null>(null)

  // True while user has picked start but not end
  const pickingTo = !single && from !== '' && to === ''

  // ── grid cells ───────────────────────────────────────────────────────────────

  const totalDays = daysInMonth(viewYear, viewMonth)
  // Sunday-first: how many blank leading cells?
  const firstDow = dowSundayFirst(new Date(viewYear, viewMonth, 1).getDay())
  const cells: Array<string | null> = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= totalDays; d++) cells.push(isoDate(viewYear, viewMonth, d))
  const tail = (7 - (cells.length % 7)) % 7
  for (let i = 0; i < tail; i++) cells.push(null)

  const dayIsos = cells.filter((c): c is string => c !== null)

  // ── month navigation ─────────────────────────────────────────────────────────

  function prevMonth(): void {
    if (viewMonth === 0) setView(viewYear - 1, 11)
    else setView(viewYear, viewMonth - 1)
  }
  function nextMonth(): void {
    if (viewMonth === 11) setView(viewYear + 1, 0)
    else setView(viewYear, viewMonth + 1)
  }

  // ── selection ────────────────────────────────────────────────────────────────

  function handleDay(iso: string): void {
    if (min && iso < min) return
    if (max && iso > max) return
    if (spanExceedsCap(iso)) return
    if (single) { onChange(iso, iso); return }
    if (oneClickSingleDay) {
      // One-click single-day mode: the first click completes a 1-day range
      // so the caller can show day-part (AM/PM/Full) options immediately
      // instead of waiting for a second click on the same date.
      //
      // Selection model (standard "third click restarts"):
      //   - no range yet (to === from, a 1-day pick) → a later click extends
      //     the end; an earlier/same click restarts a 1-day range.
      //   - a COMPLETE multi-day range already exists → any click starts a
      //     fresh 1-day range at that date, rather than extending/shrinking.
      //     So from 4–8, clicking 8 (or any day) begins a new selection.
      const hasRange = from !== '' && to !== '' && to > from
      if (hasRange || from === '' || to === '' || iso <= from) {
        onChange(iso, iso)
      } else {
        onChange(from, iso)
      }
      return
    }
    if (from === '' || to !== '' || iso < from) {
      onChange(iso, '')
    } else {
      onChange(from, iso)
    }
  }

  // Effective end for drawing (hover preview while picking to)
  const displayTo = pickingTo && hover && hover >= from ? hover : to
  const rangeEnd = !single && displayTo !== '' && displayTo > from ? displayTo : ''

  // ── keyboard navigation ──────────────────────────────────────────────────────

  // Ref map: iso → button element, for imperative focus
  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const registerBtn = useCallback((iso: string, el: HTMLButtonElement | null): void => {
    if (el) {
      btnRefs.current.set(iso, el)
    } else {
      btnRefs.current.delete(iso)
    }
  }, [])

  // When focusedDay changes, imperatively focus the corresponding button
  useEffect(() => {
    if (!focusedDay) return
    const el = btnRefs.current.get(focusedDay)
    if (el) el.focus()
  }, [focusedDay])

  function handleGridKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (!focusedDay) return
    let next: string | null = null

    switch (e.key) {
      case 'ArrowLeft':
        next = addDays(focusedDay, -1)
        break
      case 'ArrowRight':
        next = addDays(focusedDay, 1)
        break
      case 'ArrowUp':
        next = addDays(focusedDay, -7)
        break
      case 'ArrowDown':
        next = addDays(focusedDay, 7)
        break
      case 'Home': {
        // First day of week (Monday)
        const colIdx = dowSundayFirst(new Date(focusedDay).getDay())
        next = addDays(focusedDay, -colIdx)
        break
      }
      case 'End': {
        // Last day of week (Sunday)
        const colIdx = dowSundayFirst(new Date(focusedDay).getDay())
        next = addDays(focusedDay, 6 - colIdx)
        break
      }
      case 'Enter':
      case ' ':
        handleDay(focusedDay)
        e.preventDefault()
        return
      default:
        return
    }

    if (next) {
      e.preventDefault()
      // If next day is in a different month, navigate there first
      const nextYear = Number(next.slice(0, 4))
      const nextMonthNum = Number(next.slice(5, 7)) - 1
      if (nextYear !== viewYear || nextMonthNum !== viewMonth) {
        setView(nextYear, nextMonthNum)
      }
      setFocusedDay(next)
    }
  }

  const gridCols = 'repeat(7, minmax(0, 1fr))'

  return (
    <div
      data-testid="calendar-range-picker"
      style={{
        userSelect: 'none',
        width: '100%',
        ...(fillWidth ? {} : { maxWidth: 520 }),
      }}
    >
      {/*
        Mobile 44px touch targets: inject a scoped <style> block.
        Can't do @media inside inline styles — this is the CSS-only approach
        that avoids JS matchMedia causing layout flash.
      */}
      <style>{`
        @media (hover: none), (max-width: 719px) {
          #${gridId} button {
            width: 100% !important;
            min-height: 44px !important;
          }
        }
      `}</style>

      {/* Month navigation */}
      {hideNav ? null : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <button type="button" onClick={prevMonth} aria-label="Previous month" style={NAV_BTN}>
            <Icon name="chevron_left" size={18} />
          </button>
          <div
            data-testid="range-calendar-month-label"
            style={{ display: 'flex', gap: 6, alignItems: 'center' }}
          >
            <select
              aria-label="Month"
              value={viewMonth}
              onChange={(e) => setView(viewYear, Number(e.target.value))}
              style={MONTH_YEAR_SELECT}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i}>{m}</option>
              ))}
            </select>
            <select
              aria-label="Year"
              value={viewYear}
              onChange={(e) => setView(Number(e.target.value), viewMonth)}
              style={MONTH_YEAR_SELECT}
            >
              {selectableYears(viewYear, min, max).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <button type="button" onClick={nextMonth} aria-label="Next month" style={NAV_BTN}>
            <Icon name="chevron_right" size={18} />
          </button>
        </div>
      )}

      {/* Day-of-week header */}
      <div
        data-testid="dow-header"
        role="row"
        style={{ display: 'grid', gridTemplateColumns: gridCols, marginBottom: 4 }}
      >
        {DOW.map((d) => (
          <div
            key={d}
            role="columnheader"
            aria-label={d}
            style={{
              textAlign: 'center',
              fontSize: 10,
              fontWeight: 700,
              color: 'var(--text-3)',
              padding: '2px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div
        id={gridId}
        role="grid"
        aria-label={`${MONTHS[viewMonth]} ${viewYear}`}
        onKeyDown={handleGridKeyDown}
        style={{ display: 'grid', gridTemplateColumns: gridCols }}
      >
        {cells.map((iso, i) => {
          if (!iso) {
            return (
              <div
                key={`gap-${i}`}
                role="gridcell"
                aria-hidden="true"
                style={{ height: 48 }}
              />
            )
          }

          const disabled = Boolean(
            (min && iso < min) || (max && iso > max) || spanExceedsCap(iso),
          )
          const isToday = iso === todayIso
          const isFrom = from !== '' && iso === from
          const isTo = !single && to !== '' && to > from && iso === to
          const isMid = from !== '' && rangeEnd !== '' && iso > from && iso < rangeEnd
          const isRangeStart = isFrom && rangeEnd !== ''
          const isRangeEnd = rangeEnd !== '' && iso === rangeEnd && iso !== from
          const isPreviewEnd = pickingTo && iso === hover && hover !== null && hover > from
          // tab stop uses a fallback (today, else first day) so keyboard
          // users have somewhere to land when tabbing in. Visual focus
          // outline only renders on *actual* focus — never on the fallback —
          // so initial render doesn't paint a phantom ring on day 1 / today.
          const fallbackTabStop = dayIsos.includes(todayIso) ? todayIso : dayIsos[0]
          const isTabStop = iso === (focusedDay ?? fallbackTabStop)
          const isVisuallyFocused = focusedDay !== null && iso === focusedDay
          const holidayName = holidayMap.get(iso) ?? ''
          const blackoutName = blackoutMap.get(iso) ?? ''
          const isBlackout = blackoutMap.has(iso)
          const isConflict = conflictSet.has(iso)
          const outPeople = peopleOutMap.get(iso) ?? []

          // Bar background behind the circle (connects start → end).
          //
          // Three-state model when `unpaidFromDate` is supplied:
          //   • working day in annual leg → accent.bg (warm tan)
          //   • working day in unpaid leg → hatched gray (UNPAID_HATCH)
          //   • non-working day (weekend or public holiday) → NEUTRAL_BG
          // The three states make the "10 Annual + 6 Unpaid (excluding N
          // public holidays)" math line up cell-for-cell with the summary.
          //
          // When `unpaidFromDate` is absent, the whole range stays in the
          // legacy single-color fill (accent.bg).
          const UNPAID_HATCH =
            'repeating-linear-gradient(-45deg, ' +
            'rgba(120, 120, 120, 0.22) 0px, ' +
            'rgba(120, 120, 120, 0.22) 4px, ' +
            'rgba(120, 120, 120, 0.06) 4px, ' +
            'rgba(120, 120, 120, 0.06) 8px)'
          const NEUTRAL_BG = 'rgba(120, 120, 120, 0.08)'
          const overflowSplitActive =
            unpaidFromDate != null && unpaidFromDate !== ''
          const cellDow = new Date(iso + 'T00:00:00').getDay()
          const isCellWeekend = cellDow === 0 || cellDow === 6
          const isCellNonWorking =
            overflowSplitActive && (isCellWeekend || holidayMap.has(iso))
          const isUnpaidLeg = overflowSplitActive && iso >= unpaidFromDate
          const annualBg = accent.bg
          const dayBg = overflowSplitActive
            ? isCellNonWorking
              ? NEUTRAL_BG
              : isUnpaidLeg
                ? UNPAID_HATCH
                : annualBg
            : annualBg

          let outerBg: string = 'transparent'
          if (isMid) {
            outerBg = dayBg
          } else if (isRangeStart) {
            outerBg = `linear-gradient(to right, transparent 50%, ${dayBg} 50%)`
          } else if (isRangeEnd) {
            outerBg = `linear-gradient(to left, transparent 50%, ${dayBg} 50%)`
          }

          const solidCircle = isFrom || isTo
          const lightCircle = isPreviewEnd && !solidCircle

          const btn = (
            <button
              key={iso}
              type="button"
              ref={(el) => registerBtn(iso, el)}
              disabled={disabled}
              onClick={() => handleDay(iso)}
              // Suppress mouse-driven focus — pointing-device users get the
              // selection circle via `solidCircle`/`lightCircle`, so leaving
              // a 2px outline behind on every clicked-then-deselected date
              // reads as "stuck" (PM feedback 2026-05-21). Keyboard focus
              // still works because arrow keys imperatively .focus() the
              // next button via the effect on `focusedDay`.
              onMouseDown={(e) => e.preventDefault()}
              onFocus={() => setFocusedDay(iso)}
              onBlur={(e) => {
                // Clear focus state when focus leaves the calendar entirely
                // — relatedTarget is null/outside when the user clicks away.
                if (
                  e.relatedTarget === null ||
                  !(e.relatedTarget as HTMLElement).closest?.(
                    '[data-testid="calendar-range-picker"]',
                  )
                ) {
                  setFocusedDay(null)
                }
              }}
              aria-label={iso}
              aria-pressed={solidCircle}
              tabIndex={isTabStop ? 0 : -1}
              style={{
                position: 'relative',
                width: 'min(40px, 100%)',
                height: 40,
                padding: 0,
                borderRadius: '50%',
                // Today is communicated via bold weight + accent ink color
                // on the day number — no ring border. The ring read as a
                // "stuck" selection after refresh (PM feedback 2026-05-21,
                // second pass). solidCircle / lightCircle still draw their
                // own selection chrome below.
                border: 'none',
                background: solidCircle
                  ? accent.swatch
                  : lightCircle
                  ? accent.bg
                  : 'transparent',
                color: solidCircle
                  ? 'var(--surface)'
                  : disabled
                  ? 'var(--text-3)'
                  : isToday && !lightCircle
                  ? accent.swatch
                  : 'var(--text)',
                fontSize: 13,
                fontWeight: solidCircle || isToday ? 700 : 400,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                fontFamily: 'inherit',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 80ms ease-out',
                // Suppress outline on solid endpoints — the filled circle
                // already communicates selection. Showing both produces
                // a double-ring on freshly-clicked dates.
                outline: isVisuallyFocused && !solidCircle ? `2px solid ${accent.swatch}` : undefined,
                outlineOffset: isVisuallyFocused && !solidCircle ? 2 : undefined,
              }}
            >
              {Number(iso.slice(8))}
              {/* Marker dot row — holiday + conflict + blackout share the bottom band */}
              {(holidayMap.has(iso) || isConflict) ? (
                <span
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    bottom: 4,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 2,
                    pointerEvents: 'none',
                  }}
                >
                  {holidayMap.has(iso) ? (
                    <span
                      data-testid="holiday-dot"
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        // Holidays match the weekend treatment — muted
                        // grey, not amber warning.
                        background: solidCircle ? 'var(--surface)' : 'var(--text-3)',
                      }}
                    />
                  ) : null}
                  {isConflict ? (
                    <span
                      data-testid="conflict-dot"
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        background: solidCircle ? 'var(--surface)' : 'var(--text)',
                      }}
                    />
                  ) : null}
                </span>
              ) : null}
            </button>
          )

          // Wrap with tooltip(s) when there's something to show. Holiday +
          // blackout + people-out + conflict can stack.
          const hasHolidayLabel = holidayName.length > 0
          const hasBlackoutLabel = blackoutName.length > 0
          const hasPeopleOut = outPeople.length > 0

          const tooltipLines: ReactNode[] = []
          if (hasHolidayLabel) tooltipLines.push(<div key="h">Public holiday: {holidayName}</div>)
          if (hasBlackoutLabel) tooltipLines.push(<div key="b">Blackout: {blackoutName}</div>)
          if (isConflict) tooltipLines.push(<div key="c">Teammate already off</div>)
          if (hasPeopleOut) tooltipLines.push(<div key="p">{buildPeopleOutLabel(outPeople)}</div>)

          const tooltipContent: ReactNode | null =
            tooltipLines.length === 0
              ? null
              : tooltipLines.length === 1
                ? tooltipLines[0]
                : <>{tooltipLines}</>

          const wrappedBtn = tooltipContent ? (
            <Tooltip key={iso} content={tooltipContent} placement="top" delay={200}>
              {btn}
            </Tooltip>
          ) : btn

          // Bars are 40px tall (= button height) so they sit flush with the
          // circle edge rather than filling the full 48px cell.
          const rangeBar = outerBg !== 'transparent' ? (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                left: 0,
                right: 0,
                height: 40,
                background: outerBg,
                pointerEvents: 'none',
              }}
            />
          ) : null

          // Connecting bars for consecutive blackout / conflict runs.
          const prevIso = addDays(iso, -1)
          const nextIso = addDays(iso, 1)

          // Continuous pill-shaped hatched band across consecutive blackout dates.
          // No circles — borderRadius caps at run boundaries give a seamless bar.
          const HATCH = `repeating-linear-gradient(-45deg, var(--danger-bg) 0px, var(--danger-bg) 3px, transparent 3px, transparent 6px)`
          let blackoutBar: JSX.Element | null = null
          if (isBlackout && !solidCircle) {
            const lc = blackoutMap.has(prevIso)
            const rc = blackoutMap.has(nextIso)
            const borderRadius = !lc && !rc ? '20px'
              : !lc ? '20px 0 0 20px'
              : !rc ? '0 20px 20px 0'
              : '0'
            blackoutBar = (
              <div
                data-testid="blackout-cell"
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  left: 0,
                  right: 0,
                  height: 30,
                  borderRadius,
                  background: HATCH,
                  borderTop: '1px solid var(--danger-line)',
                  borderBottom: '1px solid var(--danger-line)',
                  borderLeft: lc ? 'none' : '1px solid var(--danger-line)',
                  borderRight: rc ? 'none' : '1px solid var(--danger-line)',
                  pointerEvents: 'none',
                }}
              />
            )
          }

          let conflictBarBg = 'transparent'
          if (isConflict && !solidCircle) {
            const lc = conflictSet.has(prevIso)
            const rc = conflictSet.has(nextIso)
            if (lc && rc) conflictBarBg = 'var(--border-strong)'
            else if (lc) conflictBarBg = `linear-gradient(to left, transparent 50%, var(--border-strong) 50%)`
            else if (rc) conflictBarBg = `linear-gradient(to right, transparent 50%, var(--border-strong) 50%)`
          }
          const conflictBar = conflictBarBg !== 'transparent' ? (
            <div aria-hidden="true" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: 0, right: 0, height: 40, background: conflictBarBg, pointerEvents: 'none' }} />
          ) : null

          return (
            <div
              key={iso}
              role="gridcell"
              style={{
                height: 48,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={() => { if (pickingTo) setHover(iso) }}
              onMouseLeave={() => { if (pickingTo) setHover(null) }}
            >
              {rangeBar}
              {conflictBar}
              {blackoutBar}
              {wrappedBtn}
            </div>
          )
        })}
      </div>

      {pickingTo && !hideNav ? (
        <div
          style={{
            marginTop: 8,
            textAlign: 'center',
            fontSize: 11,
            color: 'var(--text-3)',
            fontStyle: 'italic',
          }}
        >
          Pick an end date
        </div>
      ) : null}
    </div>
  )
}

/** Year options for the header dropdown. Honours min/max when given; else
 *  spans 80 years back (covers birth dates) to 10 years forward (contract
 *  / probation end dates). Always includes the current view year. */
function selectableYears(viewYear: number, min?: string, max?: string): number[] {
  const now = new Date().getFullYear()
  const lo = Math.min(min ? Number(min.slice(0, 4)) : now - 80, viewYear)
  const hi = Math.max(max ? Number(max.slice(0, 4)) : now + 10, viewYear)
  const years: number[] = []
  for (let y = hi; y >= lo; y--) years.push(y)
  return years
}

const MONTH_YEAR_SELECT: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '4px 6px',
  cursor: 'pointer',
}

const NAV_BTN: CSSProperties = {
  width: 28,
  height: 28,
  padding: 0,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text-2)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}
